import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import { Assessment } from '@/common/types/assessment';
import {
  CustomLevel,
  CustomLevelStudentProgress,
} from '@/common/types/customLevel';
import { ScopeAndSequenceLevel } from '@/common/types/scopeAndSequence';
import { CreateActivity, CreateAttempt, Session } from '@/common/types/session';
import {
  CharacterInventory,
  ControllerType,
  DailyActivitiesState,
  EquipmentConfiguration,
  GameProgress,
  Student,
} from '@/common/types/student';
import getActivityContext from '@/common/utils/activityContext';
import {
  getFromLocalStorage,
  saveToLocalStorage,
} from '@/common/utils/localStorage';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import api from '@/db/apiClient';
import GameMapState from '@/game/GameComponents/PhaserGame/Types/IGameMapState';
import { GameSessionState } from '@/game/GameComponents/PhaserGame/Types/IGameSessionState';
import { GeneralGameState } from '@/game/GameComponents/PhaserGame/Types/IGeneralGameState';
import { HeartWordsState } from '@/game/GameComponents/PhaserGame/Types/IHeartWordsState';
import { MorphMasterState } from '@/game/GameComponents/PhaserGame/Types/IMorphMasterState';
import { MysticMatchState } from '@/game/GameComponents/PhaserGame/Types/IMysticMatchState';
import { PhonemeForgeState } from '@/game/GameComponents/PhaserGame/Types/IPhonemeForgeState';
import { SentenceBuilderState } from '@/game/GameComponents/PhaserGame/Types/ISentenceBuilderState';
import { WordRiftsState } from '@/game/GameComponents/PhaserGame/Types/IWordRiftsState';
import { WordSumsState } from '@/game/GameComponents/PhaserGame/Types/IWordSumsState';
import { WordWeaverState } from '@/game/GameComponents/PhaserGame/Types/IWordWeaverState';

export class GameInstance {
  private static _instance?: GameInstance;

  static get instance(): GameInstance {
    return GameInstance._instance!;
  }

  private constructor() {
    if (GameInstance._instance) {
      throw new Error(
        'GameInstance is a singleton class and cannot be instantiated directly.',
      );
    }

    // Populate the instance with data from the API.
    this.student = {} as Student;
    this.session = {
      ...initialGameSessionState,
      ...(getFromLocalStorage('session') ?? {}),
    };
    this.assessment = null;
    this.customLevel = null;
    this.generalGameState = initialGeneralGameState;
    this.gameMap = {
      ...initialGameMapState,
      ...(getFromLocalStorage('gameMap') ?? {}),
    };
    this.mysticMatch = initialMysticMatchState;
    this.phonemeForge = initialPhonemeForgeState;
    this.wordWeaver = initialWordWeaverState;
    this.heartWords = initialHeartWordsState;
    this.sentenceBuilder = initialSentenceBuilderState;
    this.wordRifts = initialWordRiftsState;
    this.wordSums = initialWordSumsState;
    this.morphMaster = initialMorphMasterState;

    GameInstance._instance = this;
  }

  public student: Student;
  public session: GameSessionState;
  public assessment: Assessment | null;
  public customLevel: CustomLevel | null;
  public generalGameState: GeneralGameState;
  public gameMap: GameMapState;
  public mysticMatch: MysticMatchState;
  public phonemeForge: PhonemeForgeState;
  public wordWeaver: WordWeaverState;
  public heartWords: HeartWordsState;
  public sentenceBuilder: SentenceBuilderState;
  public wordRifts: WordRiftsState;
  public wordSums: WordSumsState;
  public morphMaster: MorphMasterState;

  static createGameInstance() {
    return new GameInstance();
  }

  static destroyGameInstance() {
    GameInstance._instance = undefined;
  }

  async updateStudent(newStudent: Partial<Student>) {
    const prev = this.student;
    this.student = {
      ...this.student,
      ...newStudent,
    };
    try {
      await api.asStudent.updateStudentDetails(this.student.id, this.student);
      trackMixpanelEvent('Student Updated in Game', newStudent);
    } catch (e) {
      console.error('Error updating student:', e);
      this.student = prev; // roll back in case of error
    }
  }

  async updateCustomLevelProgress(
    updates: Partial<CustomLevelStudentProgress>,
  ) {
    if (!this.customLevel) return;
    const studentId = this.student.id;
    const prev = this.customLevel;
    const current = this.customLevel.students[studentId] ?? {
      completedPlaythroughs: 0,
      gamesProgress: {},
    };
    const updatedProgress = { ...current, ...updates };
    this.customLevel = {
      ...this.customLevel,
      students: { ...this.customLevel.students, [studentId]: updatedProgress },
    };
    try {
      await api.asStudent.updateCustomLevelStudentProgress(
        this.customLevel.id,
        studentId,
        updatedProgress,
      );
    } catch (e) {
      console.error('Error updating custom level progress:', e);
      this.customLevel = prev;
    }
  }

  getActiveLevelProgress(): {
    enabledGames: string[];
    completedPlaythroughs: number;
    gamesProgress: {
      [gameName: string]: GameProgress;
    };
  } {
    if (this.customLevel && this.generalGameState.optedIntoCustomLevel) {
      const studentProgress = this.customLevel.students[this.student.id] ?? {
        completedPlaythroughs: 0,
        gamesProgress: {},
      };
      return {
        enabledGames: Object.entries(this.customLevel)
          .filter(
            ([, v]) =>
              v && typeof v === 'object' && 'enabled' in v && v.enabled,
          )
          .map(([k]) => k),
        gamesProgress: studentProgress.gamesProgress,
        completedPlaythroughs: studentProgress.completedPlaythroughs,
      };
    }
    const { level, levelsProgress } = this.student;
    const scopeLevel = (
      scopeAndSequence.levels as ScopeAndSequenceLevel[]
    ).find((l) => l.level === level);
    return {
      enabledGames: scopeLevel?.games ?? [],
      gamesProgress: levelsProgress[level - 1].gamesProgress,
      completedPlaythroughs: levelsProgress[level - 1].completedPlaythroughs,
    };
  }

  async recordGameCompleted(gameName: string) {
    const newMastery = this.session.currentActivity?.accuracy || 0;

    if (this.customLevel && this.generalGameState.optedIntoCustomLevel) {
      const studentProgress = this.customLevel.students[this.student.id] ?? {
        completedPlaythroughs: 0,
        gamesProgress: {},
      };
      const currentGameProgress = studentProgress.gamesProgress[gameName];
      await this.updateCustomLevelProgress({
        gamesProgress: {
          ...studentProgress.gamesProgress,
          [gameName]: {
            timesPlayed: (currentGameProgress.timesPlayed ?? 0) + 1,
            timesMastered:
              currentGameProgress.timesMastered + (newMastery >= 90 ? 1 : 0),
            currentHighestMastery: Math.max(
              newMastery,
              currentGameProgress.currentHighestMastery,
            ),
            completedAtNodeIndex: this.gameMap.currentNodeIndex,
          },
        },
      });
    } else {
      const student = this.student;
      const levelIdx = student.level - 1;
      const currentLevelProgress = student.levelsProgress[levelIdx];
      const currentGameProgress = currentLevelProgress.gamesProgress[gameName];
      const currentMastery = currentGameProgress.currentHighestMastery;
      const updatedStudent = structuredClone(student);
      updatedStudent.lastMasteredGame =
        currentMastery < 90 && newMastery >= 90 ? gameName : null;
      updatedStudent.levelsProgress = [
        ...student.levelsProgress.slice(0, levelIdx),
        {
          ...student.levelsProgress[levelIdx],
          gamesProgress: {
            ...currentLevelProgress.gamesProgress,
            [gameName]: {
              ...currentGameProgress,
              timesPlayed: currentGameProgress.timesPlayed + 1,
              timesMastered:
                currentGameProgress.timesMastered + (newMastery >= 90 ? 1 : 0),
              currentHighestMastery: Math.max(newMastery, currentMastery),
              completedAtNodeIndex: this.gameMap.currentNodeIndex,
            },
          },
        },
        ...student.levelsProgress.slice(levelIdx + 1),
      ];
      await this.updateStudent(updatedStudent);
    }
  }

  async recordPlaythroughCompleted() {
    const { enabledGames, gamesProgress, completedPlaythroughs } =
      this.getActiveLevelProgress();
    const isAllDone = enabledGames.every(
      (game) => (gamesProgress[game]?.timesPlayed ?? 0) > completedPlaythroughs,
    );
    if (!isAllDone) return;

    if (this.getShouldPlayCustomLevel()) {
      await this.updateCustomLevelProgress({
        completedPlaythroughs: completedPlaythroughs + 1,
      });
      await this.updateStudent({
        customLevelId: null,
      });
    } else {
      const { level, levelsProgress } = this.student;
      const levelIdx = level - 1;
      await this.updateStudent({
        levelsProgress: [
          ...levelsProgress.slice(0, levelIdx),
          {
            ...levelsProgress[levelIdx],
            completedPlaythroughs: completedPlaythroughs + 1,
          },
          ...levelsProgress.slice(levelIdx + 1),
        ],
      });
    }

    this.resetMysticMatch();
    this.resetPhonemeForge();
    this.resetWordWeaver();
    this.resetSentenceBuilder();
    this.resetWordRifts();
    this.resetHeartWords();
    this.updateGeneralGameState({ currentGame: '' });
    this.resetGameMap();
  }

  updateCharacterInventory(newCharacterInventory: Partial<CharacterInventory>) {
    this.updateStudent({
      characterInventory: {
        ...this.student.characterInventory,
        ...newCharacterInventory,
      },
    });
  }

  updateEquipmentConfiguration(
    newEquipmentConfiguration: Partial<EquipmentConfiguration>,
  ) {
    this.updateCharacterInventory({
      equippedItems: {
        ...this.student.characterInventory.equippedItems,
        ...newEquipmentConfiguration,
      },
    });
  }

  updateDailyActivities(newDailyActivities: Partial<DailyActivitiesState>) {
    this.updateStudent({
      dailyActivities: {
        ...this.student.dailyActivities,
        ...newDailyActivities,
      },
    });
  }

  resetCurrentAttemptStartedAt() {
    this.session.currentAttemptStartedAt = Date.now();
  }

  updateCurrentSession(newSession: Partial<Session>) {
    if (!this.session.currentSession) {
      console.error('No current session');
      return;
    }
    this.session.currentSession = {
      ...this.session.currentSession,
      ...newSession,
    };
  }

  async startActivity(activityName: string) {
    const { currentSession } = getSession();
    if (!currentSession) {
      console.error('No session found');
      return;
    }

    const student = getStudent();
    if (!student.id) {
      console.error('Student not found');
      return;
    }

    const assessmentId = student.assessmentId ?? undefined;
    if (activityName === 'assessment' && !assessmentId) {
      console.error('No assessment ID found');
      return;
    }

    let context = getActivityContext(
      activityName,
      student.level,
      GameInstance.instance.customLevel?.description,
    );
    const assessment = GameInstance.instance.assessment;
    if (activityName === 'assessment' && assessment) {
      const assessmentStudent = assessment.students[student.id];
      context = getActivityContext(activityName, assessmentStudent?.phase ?? 1);
    }

    const activity: Omit<CreateActivity, 'id'> = {
      version: 4,
      organisationId: student.organisationId,
      studentId: student.id,
      sessionId: currentSession.id,
      startedAt: Date.now(),
      activityName,
      context,
      accuracy: 0,
      controlTypesUsed: [],
    };
    if (assessmentId) activity.assessmentId = assessmentId;

    try {
      const newActivity = await api.asStudent.createActivity(activity);
      await api.asStudent.incrementActivityCountForSession(currentSession.id);

      this.session.currentActivity = newActivity;
      this.updateCurrentSession({
        activityCount: currentSession.activityCount + 1,
      });
      this.resetAttempts();
      this.resetActivityAccuracy();

      trackMixpanelEvent(`${activityName} Started`);
    } catch (e) {
      console.error('Error starting activity:', e);
    }
  }

  resetActivityAccuracy() {
    this.session.currentActivityAttemptCount = 0;
    this.session.currentActivityCorrectCount = 0;
  }

  async updateActivityAccuracy(correct: boolean) {
    const prev = this.session;
    if (!prev.currentActivity) {
      console.error('No current activity');
      return;
    }

    // Calculate new accuracy
    const correctCount =
      this.session.currentActivityCorrectCount + Number(correct);
    const attemptCount = this.session.currentActivityAttemptCount + 1;
    const newAccuracy = Math.min(
      100,
      Math.max(0, (correctCount / attemptCount) * 100),
    );

    // Update state
    this.session = {
      ...prev,
      currentActivity: {
        ...prev.currentActivity,
        accuracy: newAccuracy,
      },
      currentActivityCorrectCount: correctCount,
      currentActivityAttemptCount: attemptCount,
    };

    // Update db, rollback on failure
    try {
      await api.asStudent.updateActivityAccuracy(
        prev.currentActivity.id,
        newAccuracy,
      );
    } catch (e) {
      console.error('Error updaing activity accuracy:', e);
      this.session = prev;
    }
  }

  async recordActivityControlTypeUsed(control: ControllerType) {
    const { currentActivity } = this.session;
    if (!currentActivity) {
      console.error('Error recording control type for activity: no activity');
      return;
    }

    // check that current activity is of a sufficiently high version
    if (!('controlTypesUsed' in currentActivity)) {
      console.error(
        `Error recording control type for activity: current activity has no controlTypesUsed (version: ${currentActivity.version})`,
      );
      return;
    }

    // get updated value
    const prev = currentActivity;
    const updatedControlTypesUsed = [
      ...new Set(currentActivity.controlTypesUsed).add(control),
    ];

    if (updatedControlTypesUsed.length === prev.controlTypesUsed.length) {
      // nothing to update
      return;
    }

    // update state
    this.session.currentActivity = {
      ...prev,
      controlTypesUsed: updatedControlTypesUsed,
    };

    // update db
    try {
      await api.asStudent.updateActivityControlTypesUsed(
        prev.id,
        updatedControlTypesUsed,
      );
    } catch (e) {
      console.error('Error updaing activity accuracy:', e);
      this.session.currentActivity = prev;
    }
  }

  async endActivity() {
    const prev = this.session.currentActivity;
    if (!prev) {
      console.error('No current activity');
      return;
    }
    try {
      const endedAt = await api.asStudent.endActivity(prev.id);
      this.session.currentActivity = {
        ...prev,
        endedAt,
      };
    } catch (e) {
      console.error('Error ending activity:', e);
      this.session.currentActivity = prev;
    }
  }

  async addAttempt(attempt: CreateAttempt) {
    const prev = this.session.attempts;
    this.session.attempts.push(attempt);
    try {
      await api.asStudent.createAttempt(attempt);

      // record control type used on this activity
      this.recordActivityControlTypeUsed(attempt.control);
    } catch (e) {
      console.error('Error adding attempt:', e);
      this.session.attempts = prev; // Roll back in case of error
    }
  }

  resetAttempts() {
    this.session.attempts = [];
  }

  getShouldPlayCustomLevel() {
    return this.customLevel && this.generalGameState.optedIntoCustomLevel;
  }

  updateGeneralGameState(newGeneralGameState: Partial<GeneralGameState>) {
    this.generalGameState = {
      ...this.generalGameState,
      ...newGeneralGameState,
    };
  }

  updateGameMap(newGameMap: Partial<GameMapState>) {
    this.gameMap = {
      ...this.gameMap,
      ...newGameMap,
    };
    saveToLocalStorage('gameMap', this.gameMap);
  }

  resetGameMap() {
    this.gameMap = initialGameMapState;
    saveToLocalStorage('gameMap', this.gameMap);
  }

  updateMysticMatch(newMysticMatch: Partial<MysticMatchState>) {
    this.mysticMatch = {
      ...this.mysticMatch,
      ...newMysticMatch,
    };
  }

  resetMysticMatch() {
    this.mysticMatch = initialMysticMatchState;
  }

  updatePhonemeForge(newPhonemeForge: Partial<PhonemeForgeState>) {
    this.phonemeForge = {
      ...this.phonemeForge,
      ...newPhonemeForge,
    };
  }

  resetPhonemeForge() {
    this.phonemeForge = initialPhonemeForgeState;
  }

  updateWordWeaver(newWordWeaver: Partial<WordWeaverState>) {
    this.wordWeaver = {
      ...this.wordWeaver,
      ...newWordWeaver,
    };
  }

  resetWordWeaver() {
    this.wordWeaver = initialWordWeaverState;
  }

  updateHeartWords(newHeartWords: Partial<HeartWordsState>) {
    this.heartWords = {
      ...this.heartWords,
      ...newHeartWords,
    };
  }

  resetHeartWords() {
    this.heartWords = initialHeartWordsState;
  }

  updateSentenceBuilder(newSentenceBuilder: Partial<SentenceBuilderState>) {
    this.sentenceBuilder = {
      ...this.sentenceBuilder,
      ...newSentenceBuilder,
    };
  }

  resetSentenceBuilder() {
    this.sentenceBuilder = initialSentenceBuilderState;
  }

  updateWordRifts(newWordRifts: Partial<WordRiftsState>) {
    this.wordRifts = {
      ...this.wordRifts,
      ...newWordRifts,
    };
  }

  resetWordRifts() {
    this.wordRifts = initialWordRiftsState;
  }

  updateWordSums(newWordSums: Partial<WordSumsState>) {
    this.wordSums = {
      ...this.wordSums,
      ...newWordSums,
    };
  }

  resetWordSums() {
    this.wordSums = initialWordSumsState;
  }

  updateMorphMaster(newMorphMaster: Partial<MorphMasterState>) {
    this.morphMaster = {
      ...this.morphMaster,
      ...newMorphMaster,
    };
  }

  resetMorphMaster() {
    this.morphMaster = initialMorphMasterState;
  }
}

export function getCharacterInventory() {
  return GameInstance.instance.student.characterInventory;
}

export function getStudent() {
  return GameInstance.instance.student;
}

export function getDailies() {
  return GameInstance.instance.student.dailyActivities;
}

export function getGeneralGameState() {
  return GameInstance.instance.generalGameState;
}

export function getGameMap() {
  return GameInstance.instance.gameMap;
}

export function getEquipmentConfiguration() {
  return GameInstance.instance.student.characterInventory.equippedItems;
}

export function getSession() {
  return GameInstance.instance.session;
}

export function getMysticMatch() {
  return GameInstance.instance.mysticMatch;
}

export function getPhonemeForge() {
  return GameInstance.instance.phonemeForge;
}

export function getWordWeaver() {
  return GameInstance.instance.wordWeaver;
}

export function getHeartWords() {
  return GameInstance.instance.heartWords;
}

export function getSentenceBuilder() {
  return GameInstance.instance.sentenceBuilder;
}

export function getWordRifts() {
  return GameInstance.instance.wordRifts;
}

export function getWordSums() {
  return GameInstance.instance.wordSums;
}

export function getMorphMaster() {
  return GameInstance.instance.morphMaster;
}

/* Initial states */

const localStorageKeys = ['gameData', 'currentGame'];

export const initialGeneralGameState: GeneralGameState = {
  isShowingCorrectAnswer: false,
  progress: 0,
  currentGame: '',
  isRoundEnded: false,
  gameData: {},
  tipIndex: 0,
  isInteractionAllowed: true,
  lastNodeIndex: -1,
  ...localStorageKeys.reduce((acc: any, key) => {
    acc[key] = getFromLocalStorage(key);
    return acc;
  }, {}),
};

const initialGameSessionState: GameSessionState = {
  currentSession: null,
  currentActivity: null,
  attempts: [],
  currentAttemptStartedAt: 0,
  currentActivityAttemptCount: 0,
  currentActivityCorrectCount: 0,
};

const initialGameMapState: GameMapState = {
  currentNodeIndex: 0,
  playerPosition: {
    x: 0,
    y: 0,
  },
  isVisitingNode: false,
};

const initialMysticMatchState: MysticMatchState = {
  correctGraphemeCounts: {},
  dealtCards: [],
  correctCard: null,
  graphemeSet: [],
};

// Initial state of the game
const initialPhonemeForgeState: PhonemeForgeState = {
  baseWord: null,
  wordToCreate: null,
  wordsCompleted: [],
  studentAttempt: [],
  options: [],
};

const initialSentenceBuilderState: SentenceBuilderState = {
  sentenceToBuild: null,
  initialShuffledSentence: [],
  shuffledSentenceToBuild: [],
  studentAttempt: [],
  usedSentences: [],
};

const initialHeartWordsState: HeartWordsState = {
  correctHeartWordCount: {},
  dealtCards: [],
  correctCard: null,
};

const initialWordRiftsState: WordRiftsState = {
  sentenceToBuild: [],
  missingWord: '',
  dealtWords: [],
  usedWords: [],
  currentWordId: '',
};

const initialWordSumsState: WordSumsState = {
  wordToBuild: null,
  dealtMorphemes: [],
  usedWords: [],
};

const initialWordWeaverState: WordWeaverState = {
  wordToBuild: null,
  initialShuffledWord: [],
  attemptLetterCount: 0,
  studentAttempt: '',
  wordList: [],
  usedWords: [],
  remainingWord: '',
  wordAttempts: 0,
  lastLetterSoundPlayed: false,
  totalCorrectLettersGuessed: 0,
  wordsServed: 0,
  wordsGuessedCorrectly: 0,
};

const initialMorphMasterState: MorphMasterState = {
  baseWord: null,
  wordToCreate: null,
  wordsCompleted: [],
  studentAttempt: [],
  options: [],
};
